import { SupportTicketAuthorSide, SupportTicketCategory, SupportTicketPriority, SupportTicketStatus, SupportTicketType, } from '@repo/database/browser';
import { MILLISECONDS_PER_DAY } from './date.js';
/**
 * Contratto condiviso del ciclo di vita di un ticket di supporto.
 *
 * Vive in `@repo/utils` perché backend (validazione delle mutazioni) e
 * frontend (abilitazione dei comandi in UI) devono leggere la STESSA macchina a
 * stati e gli STESSI limiti: una sola tabella di transizioni, nessuna
 * duplicazione da tenere allineata. Gli enum arrivano da
 * `@repo/database/browser` (entry browser-safe, nessun PrismaClient).
 *
 * Tutte le funzioni sono pure: nessun accesso a DB, nessun side effect.
 */
const BYTES_PER_MEGABYTE = 1024 * 1024;
/** Prefisso del riferimento leggibile di un ticket (`TK-000123`). */
export const SUPPORT_TICKET_REFERENCE_PREFIX = 'TK';
/** Cifre minime del numero nel riferimento leggibile: `123` → `TK-000123`. */
const SUPPORT_TICKET_REFERENCE_DIGITS = 6;
/** Dimensione massima di un singolo allegato. */
export const SUPPORT_TICKET_MAX_ATTACHMENT_SIZE_MB = 10;
export const SUPPORT_TICKET_MAX_ATTACHMENT_SIZE_BYTES = SUPPORT_TICKET_MAX_ATTACHMENT_SIZE_MB * BYTES_PER_MEGABYTE;
/** Numero massimo di allegati per singolo messaggio. */
export const SUPPORT_TICKET_MAX_ATTACHMENTS_PER_MESSAGE = 5;
/** Numero massimo di allegati accumulabili sull'intero ticket. */
export const SUPPORT_TICKET_MAX_ATTACHMENTS_PER_TICKET = 30;
/** Lunghezza massima del corpo di un messaggio (e della descrizione iniziale). */
export const SUPPORT_TICKET_MAX_BODY_LENGTH = 10_000;
/** Lunghezza massima dell'oggetto del ticket (allineata a `@db.VarChar(200)`). */
export const SUPPORT_TICKET_MAX_SUBJECT_LENGTH = 200;
/**
 * Lunghezza massima di un singolo campo del contesto diagnostico (rotta,
 * versione del bundle, user agent, id evento Sentry). Serve a tenere fuori
 * payload abnormi da campi che il client compila da solo.
 */
export const SUPPORT_TICKET_DIAGNOSTIC_FIELD_MAX_LENGTH = 500;
/** Lunghezza massima del nome del contatto (allineata a `@db.VarChar(255)`). */
export const SUPPORT_TICKET_CONTACT_NAME_MAX_LENGTH = 255;
/** Lunghezza massima del telefono del contatto (allineata a `@db.VarChar(50)`). */
export const SUPPORT_TICKET_CONTACT_PHONE_MAX_LENGTH = 50;
/**
 * Giorni entro i quali il cliente può riaprire da solo un ticket RESOLVED o
 * CLOSED. Oltre la finestra deve aprirne uno nuovo.
 */
export const SUPPORT_TICKET_REOPEN_WINDOW_DAYS = 14;
/**
 * Giorni di permanenza in RESOLVED prima della chiusura automatica: se il
 * cliente non conferma né replica, il ticket passa a CLOSED.
 */
export const SUPPORT_TICKET_AUTO_CLOSE_DAYS = 7;
/**
 * Finestra della «prima risposta media» nelle statistiche della coda: conta
 * i ticket aperti negli ultimi N giorni. Mobile e corta di proposito, così il
 * numero racconta come sta andando il supporto adesso e non si porta dietro
 * per sempre i tempi dei primi mesi.
 */
export const SUPPORT_TICKET_FIRST_RESPONSE_WINDOW_DAYS = 30;
/**
 * Riferimento leggibile derivato dal numero progressivo. Non è persistito:
 * `SupportTicket.number` è l'unica fonte, questo è solo il modo di scriverlo.
 * Numeri oltre le cifre di padding non vengono troncati (`TK-1234567`).
 */
export function formatSupportTicketReference(ticketNumber) {
    const padded = String(ticketNumber).padStart(SUPPORT_TICKET_REFERENCE_DIGITS, '0');
    return `${SUPPORT_TICKET_REFERENCE_PREFIX}-${padded}`;
}
/**
 * Valore massimo di una colonna Postgres `integer` (INT4), il tipo di
 * `SupportTicket.number`: un numero più grande non può essere un riferimento e
 * passato a Prisma farebbe fallire la query («Value out of range»).
 */
export const POSTGRES_INT4_MAX = 2_147_483_647;
/** `TK-000123`, `tk123` o `123` → `123`. Qualsiasi altra forma → `null`. */
const SUPPORT_TICKET_REFERENCE_REGEX = /^tk[-\s]?0*(\d+)$|^0*(\d+)$/i;
/**
 * Estrae il numero progressivo da un riferimento digitato dall'utente, così la
 * ricerca in backoffice trova il ticket sia con `TK-000123` sia con `123`.
 * Restituisce `null` quando la stringa non è un riferimento valido, anche se
 * il numero supera `POSTGRES_INT4_MAX` (es. una partita IVA cercata a cifre).
 */
export function parseSupportTicketReference(raw) {
    const match = SUPPORT_TICKET_REFERENCE_REGEX.exec(raw.trim());
    if (!match)
        return null;
    const digits = match[1] ?? match[2];
    const parsed = Number(digits);
    if (!Number.isSafeInteger(parsed) || parsed <= 0)
        return null;
    if (parsed > POSTGRES_INT4_MAX)
        return null;
    return parsed;
}
/**
 * Validità della signed URL con cui si scarica un allegato.
 *
 * È un contratto a due estremi: il backend firma per questa durata, il client
 * sa entro quando spendere il link prima di richiederne un altro. Corta di
 * proposito — l'URL è un permesso di lettura senza autenticazione, e un link
 * che gira a lungo è un link che finisce dove non deve.
 */
export const SUPPORT_TICKET_ATTACHMENT_URL_TTL_SECONDS = 600;
/**
 * Chiavi i18n di titolo e messaggio delle notifiche in-app di supporto.
 *
 * Il backend non scrive testo nella notifica: salva la chiave, il frontend la
 * risolve con `t(key, data)` interpolando `SupportTicketNotificationDataDto`
 * (`reference`, `subject`). Le chiavi vivono qui perché produttore (API) e
 * consumatore (webapp) devono usare la STESSA stringa: una costante condivisa
 * al posto di due letterali da tenere allineati a mano.
 *
 * Dizionario: `apps/web/src/lib/i18n/locales/{it,en}/layout.json`, sotto
 * `notifications` (namespace unico, i moduli sono fusi in `translation`).
 */
export const SUPPORT_TICKET_NOTIFICATION_KEYS = {
    /** Risposta pubblica di un operatore. */
    REPLIED: {
        title: 'notifications.support_ticket_replied.title',
        message: 'notifications.support_ticket_replied.message',
    },
    /** Transizione di stato decisa dall'operatore (o riapertura). */
    STATUS_CHANGED: {
        title: 'notifications.support_ticket_status_changed.title',
        message: 'notifications.support_ticket_status_changed.message',
    },
};
/**
 * Stati in cui il ticket è ancora "vivo" e conta nella coda del supporto.
 *
 * Esportata perché il backend la usa anche come filtro di query (`active=true`
 * sulla lista): la definizione di "attivo" deve restare una sola, non una
 * `in` list riscritta a mano nel service.
 */
export const ACTIVE_SUPPORT_TICKET_STATUSES = [
    SupportTicketStatus.OPEN,
    SupportTicketStatus.IN_PROGRESS,
    SupportTicketStatus.WAITING_CUSTOMER,
];
export function isActiveSupportTicketStatus(status) {
    return ACTIVE_SUPPORT_TICKET_STATUSES.includes(status);
}
/**
 * Tabella delle transizioni per lato autore.
 *
 * L'operatore governa tutto il ciclo di vita. Il cliente può solo chiudere e
 * riaprire (la riapertura ha anche il vincolo temporale di
 * `canCustomerReopen`): non può mai dichiarare risolto un ticket né metterlo in
 * attesa di sé stesso. Il sistema esegue solo l'auto-chiusura dei RESOLVED.
 */
const SUPPORT_TICKET_TRANSITIONS = {
    [SupportTicketAuthorSide.OPERATOR]: {
        [SupportTicketStatus.OPEN]: [
            SupportTicketStatus.IN_PROGRESS,
            SupportTicketStatus.WAITING_CUSTOMER,
            SupportTicketStatus.RESOLVED,
            SupportTicketStatus.CLOSED,
        ],
        [SupportTicketStatus.IN_PROGRESS]: [
            SupportTicketStatus.WAITING_CUSTOMER,
            SupportTicketStatus.RESOLVED,
            SupportTicketStatus.CLOSED,
            SupportTicketStatus.OPEN,
        ],
        [SupportTicketStatus.WAITING_CUSTOMER]: [
            SupportTicketStatus.IN_PROGRESS,
            SupportTicketStatus.RESOLVED,
            SupportTicketStatus.CLOSED,
        ],
        [SupportTicketStatus.RESOLVED]: [
            SupportTicketStatus.IN_PROGRESS,
            SupportTicketStatus.CLOSED,
        ],
        [SupportTicketStatus.CLOSED]: [SupportTicketStatus.IN_PROGRESS],
    },
    [SupportTicketAuthorSide.CUSTOMER]: {
        [SupportTicketStatus.OPEN]: [SupportTicketStatus.CLOSED],
        [SupportTicketStatus.IN_PROGRESS]: [SupportTicketStatus.CLOSED],
        [SupportTicketStatus.WAITING_CUSTOMER]: [
            SupportTicketStatus.IN_PROGRESS,
            SupportTicketStatus.CLOSED,
        ],
        [SupportTicketStatus.RESOLVED]: [
            SupportTicketStatus.IN_PROGRESS,
            SupportTicketStatus.CLOSED,
        ],
        [SupportTicketStatus.CLOSED]: [SupportTicketStatus.IN_PROGRESS],
    },
    [SupportTicketAuthorSide.SYSTEM]: {
        [SupportTicketStatus.OPEN]: [],
        [SupportTicketStatus.IN_PROGRESS]: [],
        [SupportTicketStatus.WAITING_CUSTOMER]: [],
        [SupportTicketStatus.RESOLVED]: [SupportTicketStatus.CLOSED],
        [SupportTicketStatus.CLOSED]: [],
    },
};
/**
 * Stati raggiungibili da `from` per quel lato autore, nell'ordine in cui la UI
 * li propone. Una transizione verso lo stato corrente non è mai inclusa.
 */
export function getAllowedTransitions(from, actorSide) {
    return SUPPORT_TICKET_TRANSITIONS[actorSide][from];
}
/**
 * Unico giudice di una transizione di stato. Un no-op (`from === to`) è sempre
 * rifiutato: non è una transizione, ed evita eventi di storico vuoti.
 */
export function canTransition(from, to, actorSide) {
    if (from === to)
        return false;
    return getAllowedTransitions(from, actorSide).includes(to);
}
/**
 * Il cliente può riaprire solo un ticket già risolto o chiuso e solo entro
 * `SUPPORT_TICKET_REOPEN_WINDOW_DAYS` giorni (estremo incluso) dall'ancora
 * temporale, che è `closedAt` se presente, altrimenti `resolvedAt`. Senza
 * ancora la riapertura è negata: non sapremmo da quando contare.
 */
export function canCustomerReopen({ status, resolvedAt, closedAt, now = new Date(), }) {
    const isReopenable = status === SupportTicketStatus.RESOLVED ||
        status === SupportTicketStatus.CLOSED;
    if (!isReopenable)
        return false;
    const anchor = closedAt ?? resolvedAt;
    if (!anchor)
        return false;
    const deadline = anchor.getTime() + SUPPORT_TICKET_REOPEN_WINDOW_DAYS * MILLISECONDS_PER_DAY;
    return now.getTime() <= deadline;
}
/**
 * Stato del ticket → variante del badge. Unica mappa per webapp e backoffice:
 * due mappe divergenti mostrerebbero lo stesso stato con colori diversi a
 * cliente e operatore, che è esattamente l'equivoco da evitare quando i due
 * guardano lo stesso ticket.
 *
 * `Record` completo e non parziale: aggiungere un valore all'enum senza dargli
 * un colore diventa un errore di compilazione, non un badge grigio a runtime.
 *
 * Toni del design system (decisione D-T5a, 2026-10-13, chiude R-01): ogni
 * stato vivo ha un colore suo — aperto `info`, in lavorazione `violet`, in
 * attesa del cliente `warning` — e i conclusi si leggono da soli: risolto
 * `success`, chiuso `neutral`. Niente `default` (brand pieno), che il
 * backoffice riserva alle azioni.
 */
export const SUPPORT_TICKET_STATUS_BADGE_VARIANT = {
    [SupportTicketStatus.OPEN]: 'info',
    [SupportTicketStatus.IN_PROGRESS]: 'violet',
    [SupportTicketStatus.WAITING_CUSTOMER]: 'warning',
    [SupportTicketStatus.RESOLVED]: 'success',
    [SupportTicketStatus.CLOSED]: 'neutral',
};
/**
 * Priorità → variante del badge. Stessa regola della mappa degli stati: la
 * scala sale da `muted` (bassa, solo testo) a `danger` (urgente, tinta tenue
 * come gli altri toni: il rosso pieno resta alle azioni distruttive).
 */
export const SUPPORT_TICKET_PRIORITY_BADGE_VARIANT = {
    [SupportTicketPriority.LOW]: 'muted',
    [SupportTicketPriority.MEDIUM]: 'neutral',
    [SupportTicketPriority.HIGH]: 'warning',
    [SupportTicketPriority.URGENT]: 'danger',
};
/**
 * Etichette italiane canoniche del vocabolario ticket.
 *
 * Le due app traducono con i18n e non usano queste mappe; servono al server,
 * che scrive email (corpo e OGGETTO) in italiano per scelta — il destinatario
 * è il team di supporto Previxio. Stanno qui, e non nei template, perché il
 * testo deve coincidere con quello che l'operatore legge in backoffice: è la
 * stessa etichetta, non due traduzioni parallele da tenere allineate.
 */
export const SUPPORT_TICKET_TYPE_LABELS_IT = {
    [SupportTicketType.ASSISTANCE]: 'Assistenza',
    [SupportTicketType.TRAINING]: 'Formazione',
    [SupportTicketType.COMMERCIAL]: 'Commerciale',
};
export const SUPPORT_TICKET_CATEGORY_LABELS_IT = {
    [SupportTicketCategory.ELECTRONIC_INVOICING]: 'Fatturazione elettronica',
    [SupportTicketCategory.BANKING_RECONCILIATION]: 'Banche e riconciliazione',
    [SupportTicketCategory.FORECAST_DSCR]: 'Previsionale e DSCR',
    [SupportTicketCategory.PAYMENTS]: 'Pagamenti',
    [SupportTicketCategory.DOCUMENTS]: 'Documenti',
    [SupportTicketCategory.WAREHOUSE_PRODUCTS]: 'Magazzino e prodotti',
    [SupportTicketCategory.COMMESSE]: 'Commesse',
    [SupportTicketCategory.INTEGRATIONS]: 'Integrazioni',
    [SupportTicketCategory.AI_ASSISTANT]: 'Assistente AI',
    [SupportTicketCategory.BILLING_SUBSCRIPTION]: 'Abbonamento e fatturazione Previxio',
    [SupportTicketCategory.ACCOUNT_PERMISSIONS]: 'Account e permessi',
    [SupportTicketCategory.OTHER]: 'Altro',
};
//# sourceMappingURL=support-tickets.js.map